import React, { useEffect, useMemo, useRef, useState } from 'react';
import { View, ScrollView, StyleSheet, TouchableOpacity, useWindowDimensions, FlatList, Pressable, RefreshControl } from 'react-native';
import Icon from 'react-native-vector-icons/MaterialCommunityIcons';
import { appColors, appFonts, fontSize } from '../../../themes/appTheme';
import { ZenBadge, ZenEmptyComponent, ZenIcon, ZenLoader, ZenText, ZenToast, ZenDateTimePicker } from 'zen-core-elements';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import CommonService from '../../../shared/services/commonService';
import CommonHeader from '../../../shared/commonComponents/commonHeader';
import { useAppNavigation, useAppRoute } from '../../../types/@react-navigation/navigation';
import { useIsFocused } from '@react-navigation/native';
import { getAllPaymentReport } from '../../../redux/slices/paymentReport.slice';
import { CustomModal } from '../../../shared/commonComponents/customModal';
import { PosOrderReport } from './paymentReport.module';
import moment from 'moment';
import SellscreenModal from '../../newSellscreen/sellscreenModal';
import { showApiError } from '../../../shared/utils/handleApiError';


interface InfoItemProps {
    label: string;
    value: number | undefined;
    color?: string;
    isAmount?: boolean;
    currencySymbol?: string;
    format?: (val: number | undefined) => string;
}

// Define keys that are guaranteed to be PaymentBreakdown
type ReportKey = Exclude<keyof PosOrderReport, 'reportDate'>;

// Categories to iterate through
const categories: { key: ReportKey, label: string, icon: string, color: string }[] = [
    { key: 'card', label: 'Card Payments', icon: 'credit-card-outline', color: '#6366F1' },
    { key: 'case', label: 'Cash (Case)', icon: 'cash-register', color: '#10B981' },
    { key: 'loyalty', label: 'Loyalty Points', icon: 'star-outline', color: '#F59E0B' },
    { key: 'giftCard', label: 'Gift Cards', icon: 'gift-outline', color: '#EC4899' },
    { key: 'onAccount', label: 'On Account', icon: 'account-cash-outline', color: '#3B82F6' },
    { key: 'other', label: 'Other Methods', icon: 'dots-horizontal-circle-outline', color: '#94A3B8' },
];

const InfoItem = ({ label, value, color = appColors.dark, isAmount = false, currencySymbol, format }: InfoItemProps) => (
    <View style={styles.infoTile}>
        <ZenText style={styles.infoLabel} content={label} color={appColors.disabledGray500} size={fontSize.badgeFontSize} />
        <ZenText style={{ color }} content={isAmount ? CommonService.currencyStandard(currencySymbol ?? '', String(value ?? 0)) : (value ?? 0)} size={fontSize.smallFontSize} bold />
    </View>
);

const SummaryItem = ({ currencySymbol, format, value, title, icon, color, isLandscape }: { currencySymbol: string, format: (val: number | undefined) => string, value: number, title: string, icon: string, color: string, isLandscape: boolean }) => (
    <View style={[styles.topSummaryBox, !isLandscape && styles.topSummaryBoxPortrait]}>
        {isLandscape ? (
            <>
                <View style={[styles.iconContainer, { backgroundColor: color + '15', marginBottom: 12 }]}>
                    <ZenIcon type='material-community' name={icon} size={22} color={color} />
                </View>
                <ZenText style={styles.tileLabel} content={title} color={appColors.lightDark} size={fontSize.smallHeaderFontSize} />
                <ZenText style={styles.tileValue} bold content={CommonService.currencyStandard(currencySymbol, String(value ?? 0))} color={appColors.dark} size={fontSize.largerFontSize} />
            </>
        ) : (
            <View style={styles.summaryPortraitContent}>
                <View style={[styles.iconContainer, { backgroundColor: color + '15' }]}>
                    <ZenIcon type='material-community' name={icon} size={22} color={color} />
                </View>
                <View style={styles.summaryTextContainer}>
                    <ZenText content={title} color={appColors.lightDark} size={fontSize.errorFontSize} style={styles.tileLabelPortrait} />
                    <ZenText bold content={CommonService.currencyStandard(currencySymbol, String(value ?? 0))} color={appColors.dark} size={fontSize.mediumFontSize} />
                </View>
            </View>
        )}
    </View>
)

{/* RIGHT PANEL (Full Detailed Dashboard) */ }

const RenderDetailedView = ({ selectedItem, currencySymbol, format }: { selectedItem: PosOrderReport, format: (val: number | undefined) => string, currencySymbol: string }) => {
    const { width, height } = useWindowDimensions();
    const isLandscape = width > height;

    return (
        <View style={styles.mainContent}>
            <View style={styles.contentHeader}>
                <ZenText content="Transaction Overview" bold size={fontSize.largerFontSize} color={appColors.primary} />
                <ZenText content={`Report for ${selectedItem?.reportDate}`} color={appColors.lightDark} size={fontSize.smallHeaderFontSize} />
            </View>

            <ScrollView contentContainerStyle={styles.scrollContent} showsVerticalScrollIndicator={false} >
                {/* 1. TOP SUMMARY ROW */}
                <View style={styles.topSummaryRow}>
                    <SummaryItem currencySymbol={currencySymbol} format={format} value={selectedItem?.total?.total} title="Total Amount" icon='finance' color='#6366F1' isLandscape={isLandscape} />
                    <SummaryItem currencySymbol={currencySymbol} format={format} value={selectedItem?.total?.paymentAmount} title="Net Payment" icon='cash-check' color='#10B981' isLandscape={isLandscape} />
                    <SummaryItem currencySymbol={currencySymbol} format={format} value={(selectedItem?.total?.refundAmount!) || 0} title="Total Refund" icon='cash-refund' color='#EF4444' isLandscape={isLandscape} />
                </View>

                {/* 2. FULL BREAKDOWN TABLE/LIST */}
                <ZenText content="Full Category Breakdown" color={appColors.primary} bold size={fontSize.largerFontSize} />

                {categories.map((cat) => {
                    const data = selectedItem?.[cat?.key];
                    return (
                        <View key={cat.key} style={styles.categoryCard}>
                            <View style={styles.categoryHeader}>
                                <View style={[styles.iconContainer, { backgroundColor: cat.color + '15' }]}>
                                    <Icon name={cat.icon} size={20} color={cat.color} />
                                </View>
                                <ZenText content={cat.label} color={appColors.dark} size={fontSize.smallHeaderFontSize} bold style={{ flex: 1 }} />
                                <ZenText content={CommonService.currencyStandard(currencySymbol, String(data?.total ?? 0))} color={appColors.dark} size={fontSize.largerFontSize} bold />
                            </View>

                            {/* Detailed Data Grid for each category */}
                            {isLandscape ? (
                                <View style={styles.dataGrid}>
                                    <InfoItem label="Pay Amt" value={data?.paymentAmount} color={appColors.dark} currencySymbol={currencySymbol} format={format} isAmount={true} />
                                    <InfoItem label="Pay Count" value={data?.paymentTransactionCount} color={appColors.dark} />
                                    <InfoItem label="Refund Amt" value={data?.refundAmount!} color={data?.refundAmount! > 0 ? appColors.red500 : appColors.disabledGray500} currencySymbol={currencySymbol} format={format} isAmount={true} />
                                    <InfoItem label="Refund Count" value={data?.refundTransactionCount} color={data?.refundAmount! > 0 ? appColors.red500 : appColors.disabledGray500} />
                                </View>
                            ) : (
                                <View style={styles.dataGridPortrait}>
                                    <View style={styles.dataGridRowPortrait}>
                                        <InfoItem label="Pay Amt" value={data?.paymentAmount} color={appColors.dark} currencySymbol={currencySymbol} format={format} isAmount={true} />
                                        <InfoItem label="Pay Count" value={data?.paymentTransactionCount} color={appColors.dark} />
                                    </View>
                                    <View style={styles.dataGridRowPortrait}>
                                        <InfoItem label="Refund Amt" value={data?.refundAmount!} color={data?.refundAmount! > 0 ? appColors.red500 : appColors.disabledGray500} currencySymbol={currencySymbol} format={format} isAmount={true} />
                                        <InfoItem label="Refund Count" value={data?.refundTransactionCount} color={data?.refundAmount! > 0 ? appColors.red500 : appColors.disabledGray500} />
                                    </View>
                                </View>
                            )}
                        </View>
                    );
                })}
            </ScrollView>
        </View>
    )
}
let offset = 0;

const FullDataRevenueReport = () => {

    const isFocused = useIsFocused()
    const dispatch = useAppDispatch()
    const navigation = useAppNavigation();
    const { id: locationId } = useAppSelector(state => state?.common?.currentLocation)

    const route = useAppRoute("PaymentReport")
    const { width, height } = useWindowDimensions();
    const isLandscape = useMemo(() => width > height, [width, height])

    const currencySymbol = useAppSelector((state) => state?.common?.currentUser?.value?.purchasedCurrency?.currencySymbol) ?? '₹'
    const { error, loading, value, isReached, filter } = useAppSelector(state => state.paymentReport.getAllPaymentReport)
    const [selectedItem, setSelectedItem] = useState<PosOrderReport | null>(null);
    //variable for handle refresh control
    const [listRefresh, setListRefresh] = useState(false)
    const [modalVisible, setModalVisible] = useState(false)
    //Variable to handle open state of the date picker component
    const [datePickerVisibile, setDatePickerVisible] = useState<{
        visibilty: boolean,
        current: 'from' | 'to' | null
    }>({
        visibilty: false, current: null
    })

    const [dateRange, setDateRange] = useState<{ from: string; to: string }>({
        from: route?.params?.beginDate!,
        to: route?.params?.endDate!,
    });
    const format = (val: number | undefined) => (val ?? 0).toLocaleString(undefined, { minimumFractionDigits: 0 });

    const activeItem = useMemo(() => {
        if (selectedItem) return selectedItem;
        if (value?.paymentReport?.getPosOrderReport?.length! > 0) {
            return value?.paymentReport?.getPosOrderReport?.[0]!;
        }
        return null;
    }, [selectedItem, isLandscape, value]);

    useEffect(() => {
        if (isFocused) {
            offset = 0;
            setSelectedItem(null);
            getAllPaymentReports(listRefresh ? 0 : offset, listRefresh);
        }
    }, [isFocused, dateRange]);

    const prevIsLandscape = useRef<boolean>(isLandscape);

    useEffect(() => {
        if (prevIsLandscape.current === true && !isLandscape) {
            if (activeItem) {
                setModalVisible(true);
            }
        } else if (prevIsLandscape.current === false && isLandscape) {
            setModalVisible(false);
        }
        prevIsLandscape.current = isLandscape;
    }, [isLandscape, activeItem]);


    const getAllPaymentReports = (currentOffset?: number, forceRefresh = false, range = dateRange) => {
        const payload = {
            limit: 10,
            offset: currentOffset !== undefined ? currentOffset : 0,
            filterData: JSON.stringify({ customDateRange: { begin: range?.from, end: range?.to } }),
            locationId: locationId
        }
        if (!forceRefresh && payload.offset === 0 && filter === payload.filterData && value) return
        dispatch(getAllPaymentReport({
            data: payload
        })).unwrap().catch(showApiError).finally(() => {
            setListRefresh(false)
        })
    }
    if (error) {
        ZenToast({ type: 'error', message: error });
    }
    const onRequestClose = () => {
        setModalVisible(false)
        setSelectedItem(null)
    }
    const renderItem = ({ item }: any) => {
        const isSelected = isLandscape
            ? activeItem?.reportDate === item?.reportDate
            : selectedItem?.reportDate === item?.reportDate;

        return (
            <TouchableOpacity
                key={item.reportDate}
                onPress={() => {
                    if (!isLandscape)
                        setModalVisible(true)
                    setSelectedItem(item)
                }}
                style={[styles.sideCard, isSelected && styles.sideCardActive]}
            >
                <View style={styles.sideCardHeader}>
                    <ZenText color={appColors.dark} bold content={item.reportDate} size={fontSize.smallHeaderFontSize} />
                    <ZenBadge
                        badgeColor={appColors.coolGray}
                        badgeName={`${item.total?.paymentTransactionCount} Orders`}
                        badgeTextColor={appColors.lightDark}
                        badgeTextSize={fontSize.badgeFontSize}
                        badgeTextStyle={{ fontFamily: appFonts.bold }}
                        badgeContainerStyle={{
                            paddingVertical: 2,
                            alignSelf: "center"
                        }}
                    />
                </View>
                <ZenText content={CommonService.currencyStandard(currencySymbol, String(item?.total?.total ?? 0))} color={appColors.dark} bold size={fontSize.smallerFontSize} />

            </TouchableOpacity>
        )
    }

    const onRefresh = () => {
        setListRefresh(true);
        offset = 0;
        const resetRange = { from: route?.params?.beginDate!, to: route?.params?.endDate! };
        setDateRange(resetRange);
        setSelectedItem(null);
        getAllPaymentReports(0, true, resetRange);
    };

    return (
        <View style={styles.container}>
            <CommonHeader
                headerName="Payment Report"
                backIcon={true}
                backIconOnpress={() => navigation.goBack()}
            />
            {(loading && !offset && (!listRefresh || !value?.paymentReport?.getPosOrderReport?.length)) ? <ZenLoader lottie={require("../../../assets/infinityLoader.json")} lottieType='Local' /> :
                <View style={styles.layout}>

                    {/* Sidebar - List of Dates */}
                    <View style={[styles.sidebar, { flex: isLandscape ? ((value?.paymentReport?.getPosOrderReport?.length ?? 0) > 0 ? 0.3 : 1) : 1 }]}>
                        {/* NEW DATE RANGE PICKER SECTION */}
                        <View style={styles.dateFilterContainer}>
                            <ZenText
                                content="Filter Period"
                                color={appColors.lightDark}
                                size={fontSize.errorFontSize}
                                style={{ marginBottom: 8, marginLeft: 4, textTransform: 'uppercase' }}
                                bold
                            />
                            <View style={styles.dateInputsRow}>
                                {/* From Date */}
                                <TouchableOpacity
                                    style={styles.dateBox}
                                    onPress={() => setDatePickerVisible({ visibilty: true, current: 'from' })}
                                >
                                    <ZenIcon type='material-community' name="calendar-range" size={18} color={appColors.blue600} />
                                    <View style={{ marginLeft: 8 }}>
                                        <ZenText content="From" size={fontSize?.errorFontSize} color={appColors.lightDark} />
                                        <ZenText content={dateRange.from || "Select"} size={fontSize.tinyFontSize} bold color={appColors.dark} />
                                    </View>
                                </TouchableOpacity>

                                <View style={styles.dateDivider} />

                                {/* To Date */}
                                <TouchableOpacity
                                    style={styles.dateBox}
                                    onPress={() => setDatePickerVisible({ visibilty: true, current: 'to' })}
                                >
                                    <ZenIcon type='material-community' name="calendar-check" size={18} color={appColors.blue600} />
                                    <View style={{ marginLeft: 8 }}>
                                        <ZenText content="To" size={fontSize?.errorFontSize} color={appColors.lightDark} />
                                        <ZenText content={dateRange.to || "Select"} size={fontSize.tinyFontSize} bold color={appColors.dark} />
                                    </View>
                                </TouchableOpacity>
                            </View>
                        </View>
                        <FlatList
                            data={value?.paymentReport?.getPosOrderReport || []}
                            refreshControl={<RefreshControl refreshing={listRefresh} onRefresh={onRefresh} />}
                            renderItem={renderItem}
                            keyExtractor={(item, index) => item?.reportDate || index.toString()}
                            showsVerticalScrollIndicator={false}
                            onEndReached={() => {
                                if (!isReached && !loading) {
                                    offset = offset + 10;
                                    getAllPaymentReports(offset);
                                }
                            }}
                            key={isLandscape ? '1' : '2'}
                            numColumns={isLandscape ? 1 : 2}
                            onEndReachedThreshold={0.5}
                            columnWrapperStyle={!isLandscape && (value?.paymentReport?.getPosOrderReport?.length ?? 0) > 0 ? { gap: 15 } : undefined}
                            ListFooterComponentStyle={{ height: (value?.paymentReport?.getPosOrderReport?.length ?? 0) > 0 ? 10 : undefined }}
                            ListFooterComponent={!isReached && (value?.paymentReport?.getPosOrderReport?.length ?? 0) > 0 ? (
                                <View style={{ flex: 1, justifyContent: "center", alignItems: "center", height: 50 }}>
                                    <ZenLoader size={"small"} color={appColors?.primary} />
                                </View>
                            ) : null}
                            ListEmptyComponent={<ZenEmptyComponent />}
                            contentContainerStyle={{
                                paddingHorizontal: 10,
                                paddingBottom: (value?.paymentReport?.getPosOrderReport?.length ?? 0) > 0 ? 10 : 20,
                                flexGrow: (value?.paymentReport?.getPosOrderReport?.length ?? 0) > 0 ? undefined : 1,
                                gap: (value?.paymentReport?.getPosOrderReport?.length ?? 0) > 0 ? 15 : undefined,
                            }}
                        />
                    </View>

                    {isLandscape && (value?.paymentReport?.getPosOrderReport?.length ?? 0) > 0 && <View style={{ flex: 0.7 }}>
                        {activeItem && <RenderDetailedView format={format} selectedItem={activeItem} currencySymbol={currencySymbol!} />}
                    </View>}
                </View>}

            {/* Mobile Modal */}
            <SellscreenModal isModalOpen={modalVisible && !isLandscape} setModalOpen={setModalVisible} onCloseRequest={onRequestClose} viewStyle={{ padding: 0 }} >
                <>
                    <View style={{
                        width: "100%",
                        padding: 15,
                        backgroundColor: appColors?.lightBackgroundColor,
                        flexDirection: "row",
                        alignItems: "center",
                        justifyContent: "space-between",
                        paddingTop: 10,
                    }}>
                        <ZenText content={"Payment Report"} bold={true} size={width > 600 ? fontSize?.mediumFontSize : fontSize?.smallerFontSize} color={appColors?.dark} />
                        <TouchableOpacity onPress={onRequestClose} >
                            <ZenIcon name='close-circle' type='material-community' size={width > 600 ? 24 : 22} color={appColors?.iconColor} />
                        </TouchableOpacity>
                    </View>
                    <View style={{ height: height * 0.7 }}>
                        <RenderDetailedView selectedItem={activeItem!} format={format} currencySymbol={currencySymbol!} />
                    </View>
                </>
            </SellscreenModal>
            {
                datePickerVisibile?.visibilty && <ZenDateTimePicker
                    date={datePickerVisibile?.current === 'from' ? new Date(dateRange?.from) : new Date(dateRange?.to)}
                    mode="date"
                    open={datePickerVisibile?.visibilty}
                    maximumDate={
                        datePickerVisibile?.current == 'from' && dateRange?.to
                            ? new Date(dateRange?.to)
                            : new Date()
                    }
                    minimumDate={
                        datePickerVisibile?.current !== 'from' && dateRange?.from
                            ? new Date(dateRange?.from)
                            : undefined
                    }
                    onConfirm={(data) => {
                        setDatePickerVisible({
                            current: null,
                            visibilty: false
                        });

                        if (datePickerVisibile?.current == 'from') {
                            setDateRange(prev => {
                                return {
                                    ...prev,
                                    from: moment(data).format('YYYY-MM-DD')
                                }
                            })
                        }
                        else {
                            setDateRange(prev => {
                                return {
                                    ...prev,
                                    to: moment(data).format('YYYY-MM-DD')
                                }
                            })
                        }
                    }}
                    onCancel={() => {
                        setDatePickerVisible({ visibilty: false, current: null });
                    }}
                />
            }
        </View>
    );
};

const styles = StyleSheet.create({
    container: { flex: 1 },
    layout: { flex: 1, flexDirection: 'row', backgroundColor: appColors.lightBackgroundColor },

    // Sidebar Styling (Screenshot inspired)
    sidebar: { borderRightWidth: 1, borderRightColor: appColors.coolGray, },
    sideCard: { padding: 16, borderRadius: 16, backgroundColor: appColors.readDisableSwitch, borderWidth: 1, borderColor: 'transparent', flex: 1 },
    sideCardActive: { backgroundColor: appColors.light, borderColor: appColors.blue600, elevation: 4 },
    sideCardHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 5 },
    orderBadge: { backgroundColor: '#CBD5E1', paddingHorizontal: 6, paddingVertical: 2, borderRadius: 4 },
    orderBadgeText: { fontSize: 10, fontWeight: '700', color: appColors.dark },

    // Right Content Styling (Full Data)
    mainContent: { flex: 1, paddingVertical: 10, paddingHorizontal: 15 },
    contentHeader: { borderBottomWidth: 1, borderBottomColor: '#F1F5F9' },
    headerDate: { fontSize: 14, color: '#64748B', marginTop: 2 },
    scrollContent: { paddingVertical: 20, gap: 15 },

    topSummaryRow: { flexDirection: 'row', flexWrap: 'wrap', justifyContent: 'space-between', marginHorizontal: 3 },
    topSummaryBox: { width: '31%', backgroundColor: appColors.light, padding: 20, borderRadius: 24, elevation: 2 },
    topSummaryBoxPortrait: {
        width: '100%',
        padding: 12,
        borderRadius: 16,
        marginBottom: 10,
    },
    summaryPortraitContent: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 12,
    },
    summaryTextContainer: {
        flex: 1,
        justifyContent: 'center',
    },
    tileLabelPortrait: {
        marginBottom: 2,
    },
    dataGridPortrait: {
        flexDirection: 'column',
        gap: 8,
        marginTop: 10,
    },
    dataGridRowPortrait: {
        flexDirection: 'row',
        gap: 8,
    },

    categoryCard: { backgroundColor: appColors.light, borderRadius: 16, padding: 12, borderWidth: 1, borderColor: appColors.coolGray, marginBottom: 10 },
    categoryHeader: { flexDirection: 'row', alignItems: 'center', gap: 10 },
    iconContainer: { width: 36, height: 36, borderRadius: 10, justifyContent: 'center', alignItems: 'center' },
    categoryTotal: { fontSize: 18, fontWeight: '700', color: appColors.dark },

    dataGrid: { flexDirection: 'row', gap: 8, marginTop: 10 },
    gridItem: { flex: 1, alignItems: 'center' },
    gridVal: { fontSize: 14, fontWeight: '700', color: '#1E293B' },
    gridLab: { fontSize: 10, color: '#94A3B8', marginTop: 2, textTransform: 'uppercase' },
    tileLabel: { marginTop: 12 },
    tileValue: { marginTop: 4 },
    infoLabel: { textTransform: 'uppercase', marginBottom: 2 },
    infoTile: {
        flex: 1,
        backgroundColor: appColors.iceWhite,
        paddingVertical: 8,
        paddingHorizontal: 10,
        borderRadius: 8,
        borderWidth: 1,
        borderColor: appColors.softSlate,
        alignItems: 'flex-start',
    },
    dateFilterContainer: {
        paddingHorizontal: 10,
        marginVertical: 15
    },
    dateInputsRow: {
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: appColors.lightPrimary,
        borderRadius: 12,
        padding: 8,
        borderWidth: 1,
        borderColor: appColors.lightPrimary,
    },
    dateBox: {
        flex: 1,
        flexDirection: 'row',
        alignItems: 'center',
        paddingVertical: 4,
        paddingHorizontal: 8,
    },
    dateDivider: {
        width: 1,
        height: '70%',
        backgroundColor: appColors.coolGray,
        marginHorizontal: 4,
    },
});

export default FullDataRevenueReport;